import { useRef, type ReactNode } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { DnaHelixStrip } from './DnaHelixStrip';

const TILE_PX = 1200;
const DNA_REPEAT = 14;
const TRACK_WIDTH_PX = TILE_PX * DNA_REPEAT;

export type StickyDnaStep = {
  id: string;
  title: string;
  body?: string;
};

export type ScrollySourceCard = {
  id: string;
  title: string;
  body?: string;
};

export type ScrollyPlatformItem = {
  id: string;
  title: string;
  description: string;
  hint: string;
  icon: ReactNode;
};

export type StickyDnaScrollSectionProps = {
  steps: StickyDnaStep[];
  sourcesSectionTitle: string;
  sourceCards: ScrollySourceCard[];
  hintEyebrow: string;
  hintMainText: string;

  hintLeadIcon: ReactNode;

  hintDocNote: ReactNode;
  platformsEyebrow: string;
  powerTitle: string;
  powerSubtitle: string;
  fitPrefix: string;
  fitHighlight: string;
  fitSuffix: string;
  platforms: ScrollyPlatformItem[];
  extraBubbles?: ScrollyPlatformItem[];
  toolsLink?: ReactNode;
  overview?: ReactNode;

  infoSectionTitle?: string;
  infoBlocks?: { id: string; title: string; body: string }[];
  sectionVh?: number;
};

const BANDS = {
  s1: [0.0, 0.35] as const,
  s2: [0.0, 0.35] as const,
  s3: [0.4, 0.5] as const,
  s4: [0.48, 0.95] as const,
};

const OPACITY_BAND1 = [0, 0.03, 0.32, 0.35] as const;
const OPACITY_BAND3 = [0.4, 0.425, 0.475, 0.5] as const;
const OPACITY_BAND4 = [0.48, 0.505, 1] as const;

function g(band: readonly [number, number], local: number) {
  return band[0] + local * (band[1] - band[0]);
}

type SYP = ReturnType<typeof useScroll>['scrollYProgress'];

function useFadeIn(p: SYP, r: boolean | null, enter: number) {
  return useTransform(
    p,
    r ? [0, 1] : [Math.max(0, enter), Math.min(1, enter + 0.06), 1],
    r ? [1, 1] : [0, 1, 1],
  );
}

function useSlideIn(p: SYP, r: boolean | null, enter: number) {
  const clampedEnter = Math.max(0, Math.min(0.93, enter));
  return {
    opacity: useTransform(p, r ? [0, 1] : [clampedEnter, clampedEnter + 0.07, 1], r ? [1, 1] : [0, 1, 1]),
    x: useTransform(p, r ? [0, 1] : [clampedEnter, clampedEnter + 0.07, 1], r ? [0, 0] : [-64, 0, 0]),
  };
}

function useFloatIn(p: SYP, r: boolean | null, enter: number, span = 0.07) {
  const clampedEnter = Math.max(0, Math.min(0.93, enter));
  const end = Math.min(0.998, clampedEnter + span);
  return {
    opacity: useTransform(p, r ? [0, 1] : [0, clampedEnter, end, 1], r ? [1, 1] : [0, 0, 1, 1]),
    y: useTransform(p, r ? [0, 1] : [0, clampedEnter, end, 1], r ? [0, 0] : [20, 20, 0, 0]),
    scale: useTransform(p, r ? [0, 1] : [0, clampedEnter, end, 1], r ? [1, 1] : [0, 0, 1, 1]),
  };
}

export function StickyDnaScrollSection({
  steps,
  sourcesSectionTitle,
  sourceCards,
  hintEyebrow,
  hintMainText,
  hintLeadIcon,
  hintDocNote,
  platformsEyebrow,
  powerTitle,
  powerSubtitle,
  fitPrefix,
  fitHighlight,
  fitSuffix,
  platforms,
  extraBubbles,
  overview,
  infoSectionTitle,
  infoBlocks,
  sectionVh = 560,
  toolsLink,
}: StickyDnaScrollSectionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const r = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: rootRef,
    offset: ['start start', 'end end'],
  });

  const dnaX = useTransform(scrollYProgress, [0, 1], r ? [0, 0] : [0, -TRACK_WIDTH_PX * 0.48]);

  const bandO1 = useTransform(scrollYProgress, r ? [0, 1] : [...OPACITY_BAND1], r ? [1, 1] : [0, 1, 1, 0]);

  const bandO3 = useTransform(scrollYProgress, r ? [0, 1] : [...OPACITY_BAND3], r ? [1, 1] : [0, 1, 1, 0]);
  const bandO4 = useTransform(scrollYProgress, r ? [0, 1] : [...OPACITY_BAND4], r ? [1, 1] : [0, 1, 1]);

  const s1 = BANDS.s1;
  const stepM = [useSlideIn(scrollYProgress, r, g(s1, 0.05)), useSlideIn(scrollYProgress, r, g(s1, 0.05))];

  const s2 = BANDS.s2;
  const srcTitle = useFadeIn(scrollYProgress, r, g(s2, 0.0));
  const cardM = [
    useSlideIn(scrollYProgress, r, g(s2, 0.05)),
    useSlideIn(scrollYProgress, r, g(s2, 0.05)),
    useSlideIn(scrollYProgress, r, g(s2, 0.05)),
    useSlideIn(scrollYProgress, r, g(s2, 0.05)),
  ];

  const s3 = BANDS.s3;
  const hintOp = useFadeIn(scrollYProgress, r, g(s3, 0.1));
  const hintY = useTransform(
    scrollYProgress,
    r ? [0, 1] : [g(s3, 0.1), g(s3, 0.5), 1],
    r ? [0, 0] : [28, 0, 0],
  );

  const s4 = BANDS.s4;
  const platIntro = useFadeIn(scrollYProgress, r, g(s4, 0.0));
  const bubSpan = 0.055;
  const bubM = [
    useFloatIn(scrollYProgress, r, g(s4, 0.0), bubSpan),
    useFloatIn(scrollYProgress, r, g(s4, 0.3), bubSpan),
    useFloatIn(scrollYProgress, r, g(s4, 0.6), bubSpan),
  ];
  const extraBubM = [
    useFloatIn(scrollYProgress, r, g(s4, 0.15), bubSpan),
    useFloatIn(scrollYProgress, r, g(s4, 0.45), bubSpan),
  ];

  const bubConfig = [
    { size: 168, ox: 0, oy: 0 },
    { size: 152, ox: 24, oy: 28 },
    { size: 160, ox: -8, oy: 52 },
  ];
  const extraBubConfig = [
    { size: 140, oy: 108 },
    { size: 140, oy: 56 },
  ];

  return (
    <>
      <div ref={rootRef} className="scrolly-root" style={{ height: `${sectionVh}vh` }}>
        <div className="scrolly-sticky-frame">
          <div className="scrolly-dna-viewport" aria-hidden>
            <motion.div
              className="scrolly-dna-track"
              style={{ x: dnaX, width: TRACK_WIDTH_PX, willChange: 'transform' }}
            >
              <DnaHelixStrip repeat={DNA_REPEAT} fade="none" className="scrolly-dna-svg" />
            </motion.div>
          </div>

          <div className="scrolly-content-area">
            <motion.div
              className="scrolly-band scrolly-band--combined"
              style={{ opacity: bandO1 }}
              aria-hidden={undefined}
            >
              <div className="scrolly-combined-wrapper">
                <div className="scrolly-s1-copy">
                  {steps.slice(0, 2).map((step, i) => (
                    <motion.article
                      key={step.id}
                      className="scrolly-s1-step"
                      style={{ opacity: stepM[i].opacity, x: stepM[i].x }}
                    >
                      <h2 className="scrolly-s1-step__title">{step.title}</h2>
                      {step.body?.trim() ? <p className="scrolly-s1-step__body">{step.body}</p> : null}
                    </motion.article>
                  ))}
                </div>

                <div className="scrolly-s2-inner">
                  <motion.h2 className="scrolly-s2-title" style={{ opacity: srcTitle }}>
                    {sourcesSectionTitle}
                  </motion.h2>
                  <div className="scrolly-s2-cards" role="list">
                    {sourceCards.slice(0, 4).map((card, i) => (
                      <motion.article
                        key={card.id}
                        className="scrolly-s2-card"
                        style={{ opacity: cardM[i].opacity, x: cardM[i].x }}
                        role="listitem"
                      >
                        <span className="scrolly-s2-card__index" aria-hidden>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <h3 className="scrolly-s2-card__title">{card.title}</h3>
                        {card.body?.trim() ? <p className="scrolly-s2-card__body">{card.body}</p> : null}
                      </motion.article>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div className="scrolly-band scrolly-band--hint" style={{ opacity: bandO3 }}>
              <motion.div className="scrolly-s3-inner" style={{ opacity: hintOp, y: hintY }}>
                <p className="about-page__eyebrow scrolly-s3-eyebrow">{hintEyebrow}</p>
                <div className="scrolly-s3-lead">
                  <span className="scrolly-s3-lead__icon" aria-hidden>
                    {hintLeadIcon}
                  </span>
                  <blockquote className="scrolly-s3-quote">{hintMainText}</blockquote>
                </div>
                <p className="scrolly-s3-doc-note">{hintDocNote}</p>
              </motion.div>
            </motion.div>

            <motion.div className="scrolly-band" style={{ opacity: bandO4 }}>
              <div className="scrolly-s4-inner">
                <motion.div className="scrolly-s4-left" style={{ opacity: platIntro }}>
                  <p className="about-page__eyebrow">{platformsEyebrow}</p>
                  <h2 className="scrolly-s4-title">{powerTitle}</h2>
                  <p className="scrolly-s4-subtitle">{powerSubtitle}</p>
                  <p className="scrolly-s4-fit">
                    {fitPrefix}
                    <strong className="scrolly-s4-fit__highlight">{fitHighlight}</strong>
                    {fitSuffix}
                  </p>
                  <small>{toolsLink}</small>
                </motion.div>

                <div className="scrolly-s4-bubbles">
                  <div className="scrolly-s4-bubbles__main">
                    {platforms.slice(0, 3).map((p, i) => {
                      const { size, ox, oy } = bubConfig[i];
                      const m = bubM[i];
                      return (
                        <motion.div
                          key={p.id}
                          className="scrolly-s4-bubble"
                          style={{
                            opacity: m.opacity,
                            y: m.y,
                            scale: m.scale,
                            width: size,
                            marginLeft: ox,
                            marginTop: oy,
                          }}
                        >
                          <div className="scrolly-s4-bubble__ring" style={{ width: size, height: size }}>
                            <span className="scrolly-s4-bubble__icon">{p.icon}</span>
                            <span className="scrolly-s4-bubble__title">{p.title}</span>
                            <span className="scrolly-s4-bubble__desc">{p.description}</span>
                          </div>
                          <span className="scrolly-s4-bubble__hint">{p.hint}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                  {extraBubbles && extraBubbles.length > 0 && (
                    <div className="scrolly-s4-bubbles__extra">
                      {extraBubbles.slice(0, 2).map((p, i) => {
                        const { size, oy } = extraBubConfig[i];
                        const m = extraBubM[i];
                        return (
                          <motion.div
                            key={p.id}
                            className="scrolly-s4-bubble"
                            style={{
                              opacity: m.opacity,
                              y: m.y,
                              scale: m.scale,
                              width: size,
                              marginTop: oy,
                            }}
                          >
                            <div className="scrolly-s4-bubble__ring" style={{ width: size, height: size }}>
                              <span className="scrolly-s4-bubble__icon">{p.icon}</span>
                              <span className="scrolly-s4-bubble__title">{p.title}</span>
                              <span className="scrolly-s4-bubble__desc">{p.description}</span>
                            </div>
                            <span className="scrolly-s4-bubble__hint">{p.hint}</span>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {overview ? (
        <>
          <div className="scrolly-post-sticky-dwell" aria-hidden="true" />
          <div className="scrolly-s5-wrap scrolly-s5-wrap--foundations">{overview}</div>
        </>
      ) : (
        <section className="scrolly-s5-wrap" aria-labelledby="about-page-info-heading">
          <div className="scrolly-s5-inner">
            <h2 id="about-page-info-heading" className="about-page__info-heading">
              {infoSectionTitle}
            </h2>
            <ul className="about-page__info-grid">
              {(infoBlocks ?? []).map(block => (
                <li key={block.id}>
                  <div className="about-page__info-block">
                    <h3 className="about-page__info-block-title">{block.title}</h3>
                    <p className="about-page__info-block-body">{block.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </>
  );
}
